/**
 * useBilling Hook
 * 빌링키 발급 관련 로직을 처리하는 React Hook
 */

import { useCallback, useMemo, useState } from 'react';
import type { BillingKeyIssueRequest } from '../../../domain/models/payments/Billing';
import { IssueBillingKeyUseCase } from '../../../domain/usecases/payments/IssueBillingKeyUseCase';
import { inicisBillingRepository } from '../../../data/repositories/InicisBillingRepositoryImpl';
import { CreateSubscriptionFromSearchParamsUseCase } from '../../../domain/usecases/payments/CreateSubscriptionFromSearchParamsUseCase';

export const useBilling = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Use Case 초기화
  const issueBillingKeyUseCase = useMemo(
    () => new IssueBillingKeyUseCase(inicisBillingRepository),
    []
  );

  const createSubscriptionFromSearchParamsUseCase = useMemo(
    () => new CreateSubscriptionFromSearchParamsUseCase(inicisBillingRepository),
    []
  );

  /**
   * 빌링키 발급
   */
  const issueBillingKey = useCallback(
    async (request: BillingKeyIssueRequest) => {
      setIsLoading(true);
      setError(null);
      try {
        await issueBillingKeyUseCase.execute(request);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('빌링키 발급 중 오류가 발생했습니다.');
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [issueBillingKeyUseCase]
  );

  /**
   * 서버 주문번호 생성
   */
  const generateOrderId = useCallback(async (productId: string): Promise<string> => {
    return inicisBillingRepository.generateOrderId(productId);
  }, []);

  /**
   * 현재 타임스탬프(YYYYMMDDhhmmss)
   */
  const getCurrentTimestamp = useCallback((): string => {
    return inicisBillingRepository.getCurrentTimestampYYYYMMDDhhmmss();
  }, []);

  /**
   * 구독 생성
   */
  const approveSubscriptionFromSearchParams = useCallback(
    async (searchParams: URLSearchParams): Promise<boolean> => {
      return createSubscriptionFromSearchParamsUseCase.execute(searchParams);
    },
    [createSubscriptionFromSearchParamsUseCase]
  );

  return {
    isLoading,
    error,
    issueBillingKey,
    generateOrderId,
    getCurrentTimestamp,
    approveSubscriptionFromSearchParams
  };
};
